import GideonImg from "../images/ushindi.jpg";
import MichaelImg from "../images/Michael.webp";
import WhatsAppButton from "./Components/WhatsAppButton";
import {
  Target,
  Eye,
  ShieldCheck,
  Users,
  Lightbulb,
  ArrowUpRight,
  Code2,
} from "lucide-react";

export default function About() {
  const teamMembers = [
    {
      name: "Gideon Ushindi",
      role: "Founder & Software Engineer",
      description:
        "Gideon is a software engineer and technology enthusiast with experience building modern web, mobile, and intelligent digital systems. His work spans software engineering, machine learning, finance, education, health, and agriculture.",
      image: GideonImg,
      coffeeLink: "https://paystack.shop/pay/xwtue-o93f",
    },
    {
      name: "Michael Njenga",
      role: "Software Engineer & Cybersecurity Specialist",
      description:
        "Michael is a software engineer and cybersecurity specialist focused on secure application development, network security, and building robust digital systems that businesses and organizations can rely on.",
      image: MichaelImg,
      coffeeLink: "https://paystack.shop/pay/xwtue-o93f",
    },
  ];

  const values = [
    {
      title: "Innovation",
      description:
        "We continuously explore better ways to solve real-world problems through technology.",
      icon: Lightbulb,
    },
    {
      title: "Integrity",
      description:
        "We believe trust, transparency, and accountability are essential in every project we build.",
      icon: ShieldCheck,
    },
    {
      title: "Customer Focus",
      description:
        "We build around real user needs, business goals, and measurable outcomes.",
      icon: Users,
    },
    {
      title: "Engineering Excellence",
      description:
        "We aim to build reliable, scalable, secure, and maintainable digital products.",
      icon: Code2,
    },
  ];

  return (
    <div className="min-h-screen bg-white text-slate-900 overflow-hidden">
      {/* Hero */}
      <section className="relative pt-28 pb-24 px-6 bg-slate-950 text-white overflow-hidden">
        <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-blue-600/20 rounded-full blur-[150px]" />

        <div className="absolute -bottom-40 right-0 w-[500px] h-[500px] bg-cyan-400/10 rounded-full blur-[140px]" />

        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage: `
              linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px),
              linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)
            `,
            backgroundSize: "50px 50px",
          }}
        />

        <div className="relative z-10 max-w-5xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 mb-7 rounded-full border border-blue-400/20 bg-blue-500/10 text-blue-300 text-sm font-semibold">
            <span className="w-2 h-2 bg-blue-400 rounded-full" />
            About Nexindi Tech
          </div>

          <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold tracking-tight leading-tight mb-7">
            Building technology for
            <span className="block text-blue-400">
              real people and real problems.
            </span>
          </h1>

          <p className="text-lg md:text-xl text-slate-300 leading-relaxed max-w-3xl mx-auto">
            Nexindi Tech is a technology company focused on creating practical,
            scalable, and impactful digital solutions for businesses,
            institutions, organizations, and communities.
          </p>
        </div>
      </section>

      {/* Mission / Vision */}
      <section className="relative py-24 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-6">
            <div className="group relative p-8 md:p-10 rounded-3xl bg-blue-600 text-white overflow-hidden">
              <div className="absolute -right-20 -bottom-20 w-64 h-64 bg-white/10 rounded-full" />

              <div className="relative z-10">
                <div className="w-14 h-14 rounded-2xl bg-white/15 flex items-center justify-center mb-8">
                  <Target size={27} />
                </div>

                <span className="text-blue-200 text-sm font-semibold uppercase tracking-wider">
                  Our Mission
                </span>

                <h2 className="text-3xl md:text-4xl font-bold mt-3 mb-5">
                  Technology that creates opportunity.
                </h2>

                <p className="text-blue-100 leading-relaxed text-lg">
                  To empower businesses, institutions, and individuals through
                  efficient, accessible, and scalable digital solutions that
                  solve meaningful real-world problems.
                </p>
              </div>
            </div>

            <div className="group relative p-8 md:p-10 rounded-3xl border border-slate-200 bg-slate-50 overflow-hidden hover:border-blue-200 transition-all duration-500">
              <div className="absolute -right-20 -bottom-20 w-64 h-64 bg-blue-100 rounded-full opacity-60" />

              <div className="relative z-10">
                <div className="w-14 h-14 rounded-2xl bg-blue-100 text-blue-600 flex items-center justify-center mb-8">
                  <Eye size={27} />
                </div>

                <span className="text-blue-600 text-sm font-semibold uppercase tracking-wider">
                  Our Vision
                </span>

                <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mt-3 mb-5">
                  A digitally empowered Africa.
                </h2>

                <p className="text-slate-600 leading-relaxed text-lg">
                  To become a trusted African technology company known for
                  building innovative digital products that improve how people
                  live, work, learn, trade, and grow.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="relative py-24 px-6 bg-slate-50 overflow-hidden">
        <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-blue-100/70 rounded-full blur-[120px]" />

        <div className="relative z-10 max-w-7xl mx-auto">
          <div className="max-w-3xl mb-14">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-50 border border-blue-100 text-blue-600 text-sm font-semibold mb-5">
              <span className="w-2 h-2 bg-blue-600 rounded-full" />
              What Guides Us
            </div>

            <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-slate-900 mb-5">
              Principles behind
              <span className="text-blue-600"> everything we build.</span>
            </h2>

            <p className="text-lg text-slate-600">
              Our values shape how we work with clients, design products, and
              approach every technical challenge.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => {
              const Icon = value.icon;

              return (
                <div
                  key={index}
                  className="group bg-white border border-slate-200 rounded-3xl p-7 transition-all duration-500 hover:-translate-y-2 hover:border-blue-200 hover:shadow-xl hover:shadow-blue-900/5"
                >
                  <div className="w-12 h-12 rounded-2xl bg-blue-50 flex items-center justify-center text-blue-600 mb-6 group-hover:bg-blue-600 group-hover:text-white transition-all duration-300">
                    <Icon size={23} />
                  </div>

                  <h3 className="text-xl font-bold text-slate-900 mb-3">
                    {value.title}
                  </h3>

                  <p className="text-slate-600 text-sm leading-relaxed">
                    {value.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="relative py-24 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-50 border border-blue-100 text-blue-600 text-sm font-semibold mb-5">
              <span className="w-2 h-2 bg-blue-600 rounded-full" />
              Our Team
            </div>

            <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-slate-900 mb-5">
              The people behind
              <span className="text-blue-600"> Nexindi Tech.</span>
            </h2>

            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              A growing team of engineers and technology professionals focused
              on building useful, secure, and scalable digital products.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {teamMembers.map((member, index) => (
              <div
                key={index}
                className="group bg-white border border-slate-200 rounded-3xl overflow-hidden transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl hover:shadow-blue-900/10"
              >
                {/* Image */}
                <div className="relative h-[350px] overflow-hidden">
                  <img
                    src={member.image}
                    alt={member.name}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />

                  <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-slate-950/10 to-transparent" />

                  <div className="absolute bottom-6 left-6 right-6">
                    <h3 className="text-2xl font-bold text-white mb-1">
                      {member.name}
                    </h3>

                    <p className="text-blue-300 font-semibold">
                      {member.role}
                    </p>
                  </div>
                </div>

                {/* Content */}
                <div className="p-7">
                  <p className="text-slate-600 leading-relaxed">
                    {member.description}
                  </p>

                  {member.coffeeLink && (
                    <a
                      href={member.coffeeLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group/button mt-7 inline-flex items-center gap-2 text-sm font-semibold text-blue-600 hover:text-blue-700 transition"
                    >
                      Support our work
                      <ArrowUpRight
                        size={16}
                        className="transition-transform group-hover/button:translate-x-0.5 group-hover/button:-translate-y-0.5"
                      />
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Closing CTA */}
      <section className="px-6 pb-24 bg-white">
        <div className="max-w-7xl mx-auto rounded-[2rem] bg-gradient-to-br from-blue-600 via-blue-700 to-slate-950 px-8 py-16 md:px-16 md:py-20 text-center text-white relative overflow-hidden">
          <div className="absolute -top-32 -right-20 w-[400px] h-[400px] bg-cyan-400/15 rounded-full blur-[120px]" />

          <div className="relative z-10 max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-5xl font-bold tracking-tight mb-5">
              Want to build something with us?
            </h2>

            <p className="text-blue-100 text-lg mb-8">
              Tell us about your idea, business challenge, or digital product
              and let's explore how technology can help.
            </p>

            <a
              href="/contact"
              className="inline-flex items-center gap-3 bg-white text-blue-700 px-7 py-4 rounded-xl font-bold hover:bg-blue-50 transition-all duration-300 hover:-translate-y-1"
            >
              Start a Conversation
              <span>→</span>
            </a>
          </div>
        </div>
      </section>

      <WhatsAppButton />
    </div>
  );
}